import React from 'react';

/*
 * Logo officiel KS Tech (direction « Modules » : un K abstrait en blocs).
 * Source de vérité : design_handoff_logo_kstech/kstech-mark.svg (grille 56×56).
 *   - Barre verticale : 0,0 15×56 rx2.5
 *   - Blocs droite    : 33,0 et 33,33 23×23 rx2.5
 *   - Losange central : 21,20 16×16 rx2.5, rotate(45 29 28)
 * Typo de marque : Space Grotesk (chargée dans public/index.html).
 */

const C = {
  ink: '#262633',          // encre (barre, fond clair)
  indigo: '#4F51D6',       // indigo principal (blocs)
  indigoLight: '#9799EA',  // losange
  indigoMid: '#6467E0',    // blocs sur fond sombre
  indigoPale: '#C9CBF5',   // losange sur fond indigo
  wordmark: '#22222E',
  white: '#FFFFFF',
};

export const KSTECH_FONT = "'Space Grotesk', system-ui, -apple-system, sans-serif";

// Injecte les keyframes de l'animation une seule fois dans le document.
if (typeof document !== 'undefined' && !document.getElementById('kstech-logo-anim')) {
  const s = document.createElement('style');
  s.id = 'kstech-logo-anim';
  s.textContent =
    '@keyframes kstechSpin{from{transform:rotate(45deg)}to{transform:rotate(405deg)}}' +
    '@keyframes kstechPulse{0%,100%{opacity:1}50%{opacity:.55}}' +
    '.kstech-diamond-anim{transform-box:fill-box;transform-origin:center;' +
    'animation:kstechSpin 1.2s cubic-bezier(.45,0,.55,1) infinite,' +
    'kstechPulse 1.2s ease-in-out infinite}';
  document.head.appendChild(s);
}

// Palette selon le contexte de fond.
function palette(theme) {
  switch (theme) {
    case 'dark':       return { bar: C.white, block: C.indigoMid,  diamond: C.indigoLight, word: C.white };
    case 'mono-dark':  return { bar: '#111111', block: '#111111', diamond: '#111111', word: '#111111' };
    case 'mono-white': return { bar: C.white, block: C.white, diamond: C.white, word: C.white };
    default:           return { bar: C.ink, block: C.indigo, diamond: C.indigoLight, word: C.wordmark }; // light
  }
}

// Symbole seul (variante 2c/monochrome via theme).
export function KSTechMark({ size = 40, theme = 'light', animated = false, style, title = 'KSTECH' }) {
  const p = palette(theme);
  return (
    <svg viewBox="0 0 56 56" width={size} height={size} style={style} role="img" aria-label={title}>
      <rect x="0" y="0" width="15" height="56" rx="2.5" fill={p.bar} />
      <rect x="33" y="0" width="23" height="23" rx="2.5" fill={p.block} />
      <rect x="33" y="33" width="23" height="23" rx="2.5" fill={p.block} />
      <rect
        x="21" y="20" width="16" height="16" rx="2.5" fill={p.diamond}
        transform="rotate(45 29 28)"
        className={animated ? 'kstech-diamond-anim' : undefined}
      />
    </svg>
  );
}

/*
 * Logo complet.
 *   variant : 'symbol' | 'horizontal' | 'vertical'
 *   theme   : 'light' | 'dark' | 'mono-dark' | 'mono-white'
 *   subtitle: sous-titre optionnel (ex. 'CRM restaurant') — horizontal seulement
 */
export function KSTechLogo({ variant = 'horizontal', size = 40, theme = 'light', subtitle = null, animated = false, style }) {
  const p = palette(theme);
  const font = KSTECH_FONT;

  if (variant === 'symbol') {
    return <KSTechMark size={size} theme={theme} animated={animated} style={style} />;
  }

  if (variant === 'vertical') {
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: Math.round(size * 0.3), ...style }}>
        <KSTechMark size={size} theme={theme} animated={animated} />
        <span style={{ fontFamily: font, fontWeight: 700, fontSize: Math.round(size * 0.44), letterSpacing: 0, color: p.word, lineHeight: 1 }}>KSTECH</span>
      </div>
    );
  }

  // horizontal (symbole + espace 20/56 + bloc texte)
  const wordSize = Math.round(size / 1.47);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(size * 0.357), ...style }}>
      <KSTechMark size={size} theme={theme} animated={animated} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span style={{ fontFamily: font, fontWeight: 700, fontSize: wordSize, letterSpacing: '-0.5px', color: p.word }}>KSTECH</span>
        {subtitle && (
          <span style={{ fontFamily: font, fontWeight: 500, fontSize: Math.max(9, Math.round(wordSize * 0.29)), letterSpacing: '4.5px', color: theme === 'dark' ? C.indigoLight : C.indigo, marginTop: 3, textTransform: 'uppercase' }}>
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

/*
 * Loader animé : le losange central du symbole tourne (45°→405°, ~1.2s) avec pulse.
 * À utiliser sur tous les écrans de chargement.
 */
export function KSTechLoader({ size = 46, theme = 'light', label = null, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, ...style }}>
      <KSTechMark size={size} theme={theme} animated />
      {label && (
        <span style={{ fontFamily: KSTECH_FONT, fontSize: 14, letterSpacing: 0.3, color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : '#8a8a94' }}>
          {label}
        </span>
      )}
    </div>
  );
}

export default KSTechLogo;
